import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, OnDestroy, inject, input } from '@angular/core';
import { CropType, provideVideoIntl, VideoComponent } from '@knorr-bremse-portals/ngx-components/components/video';
import { ReplaySubject, switchMap, map } from 'rxjs';
import { EditorIdDirective } from '../../directives/editor-id.directive';
import { LocalVideoIntl } from '../../providers/intl/video.intl';
import { SafePipe } from '../../providers/safe.pipe';
import { YouTubeService } from '../../providers/youtube.service';
import { UserConsentDialogComponent } from '../user-consent-dialog/user-consent-dialog.component';
import { InnerSection, AnyInnerSection } from '../../types/section';
import { InnerSectionComponent } from '../inner-section/inner-section.component';

type VideoAlign = 'top' | 'centered' | 'bottom';

export type InternalVideoAttributes = {
  src: string;
  thumbnailSrc: string;
  align: VideoAlign;
  autoplay: boolean;
  loop: boolean;
};
export type InternalVideoEditorIds = [
  'src',
  'thumbnailSrc',
  'align',
  'autoplay',
  'loop',
];

export const VIDEO_COMPONENT_NAME = 'video';

export type InternalVideoSection = InnerSection<
  InternalVideoAttributes,
  InternalVideoEditorIds,
  typeof VIDEO_COMPONENT_NAME
>;

export type ExternalVideoAttributes = {
  embedUrl: string;
}
export type ExternalVideoEditorIds = [
  'embedUrl',
];

export function isExternalVideoSection(section: PlainVideoSection): section is ExternalVideoSection {
  return (section as ExternalVideoSection).attributes.embedUrl !== undefined;
}

export type ExternalVideoSection = InnerSection<
  ExternalVideoAttributes,
  ExternalVideoEditorIds,
  typeof VIDEO_COMPONENT_NAME
>;

export type PlainVideoSection = InternalVideoSection | ExternalVideoSection;

export function isVideoSection(section: AnyInnerSection): section is PlainVideoSection {
  return section.component === VIDEO_COMPONENT_NAME;
}

export function alignToCropType(align: VideoAlign): CropType {
  if (align === 'centered') {
    return 'both';
  }
  return align;
}

export function isInternalVideoSectionComponent(section: PlainVideoSection): section is InternalVideoSection {
  return (section as InternalVideoSection).attributes.src !== undefined;
}

@Component({
    selector: 'app-plain-video',
    imports: [
        CommonModule, EditorIdDirective, SafePipe, UserConsentDialogComponent,
        VideoComponent,
    ],
    providers: [
        provideVideoIntl(LocalVideoIntl),
    ],
    templateUrl: './plain-video.component.html',
    styleUrl: './plain-video.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlainVideoComponent extends InnerSectionComponent<PlainVideoSection> implements OnInit, OnDestroy {

  public controls = input.required<VideoComponent['controls']>();

  private youTubeService = inject(YouTubeService);
  protected userConsent = this.youTubeService.userConsent;
  protected getYouTubeEmbedUrl = YouTubeService.getEmbedUrl;

  protected youTubeVideoId$ = new ReplaySubject<string>(1);
  protected youTubeThumbnailUrl$ = this.youTubeVideoId$.pipe(
    switchMap(videoId => this.youTubeService.getVideoMetadata(videoId)),
    map(metadata => this.youTubeService.getThumbnailUrl(metadata)),
  );

  public ngOnInit(): void {
    this.setupYouTubeVideoId();
  }

  public ngOnDestroy(): void {
    this.youTubeVideoId$.complete();
  }

  private setupYouTubeVideoId(): void {
    if (!isExternalVideoSection(this.section)) {
      return;
    }

    const videoId = this.youTubeService.extractVideoId(this.section.attributes.embedUrl);
    if (videoId === null) {
      return;
    }

    this.youTubeVideoId$.next(videoId);
  }

  protected isExternalVideo = isExternalVideoSection;
  protected isInternalVideo = isInternalVideoSectionComponent;

}
